import { Button, Col, Row, Spin } from 'antd';
import React, { useEffect, useRef } from 'react';
import Title from 'antd/es/typography/Title';
import CountersHeader from './Components/CountersHeader';
import RevenueBarChart from './Components/RevenueBarChart';
import TripsLineChart from './Components/TripsLineChart';
import CO2LineChart from './Components/CO2LineChart';
import { useSortedReportsTripsQuery } from './Components/hooks/useSortedReportsTripsQuery';
import { DownloadOutlined } from '@ant-design/icons';
import { ReportsFilter } from './Components/ReportsFilter';
import { useSortedReportsCO2Query } from './Components/hooks/useSortedReportsCO2Query';
import { useSortedReportsCountersQuery } from './Components/hooks/useSortedReportsCountersQuery';
import { useOverviewLinkState } from './Components/linkstate/ReportsOverview.linkstate';
import { useGetSettingQuery } from '../../GraphQL/hooks/useGetSettingQuery';
import jwt_decode from 'jwt-decode';
import { useUserState } from '../../Hooks/useUserState';
import CountersHeaderTaxi from './Components/CountersHeaderTaxi';
import { useSortedReportsCountersTaxiBusinessQuery } from './Components/hooks/useSortedReportsCountersTaxiQuery';
import LoginMethodRidersChart from './Components/LoginMethodRidersChart';
import DriversStatus from './Components/DriversStatus';
import './Components/style.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import RidersStatus from './Components/RidersStatus';

const ReportsView = () => {
    const { countersData, refetchCounters, loadingCounters } = useSortedReportsCountersQuery();
    const { countersTaxiBusinessData, refetchTaxiBusinessCounters, loadingTaxiBusinessCounters } =
        useSortedReportsCountersTaxiBusinessQuery();
    const { tripsData, refetchTrips, loadingTrips } = useSortedReportsTripsQuery();
    const { CO2Data, refetchCO2, loadingCO2 } = useSortedReportsCO2Query();
    const [{ selectedDateRange, quickCalendarFilter }] = useOverviewLinkState();
    const reportsContainerRef = useRef<HTMLDivElement>(null); // Add type declaration
    const { ...state } = useUserState();

    const decoded: { _id: string; sectionRoles: [string] } = state?.accessToken
        ? jwt_decode(state?.accessToken)
        : undefined;
    const { data, refetch, loading } = useGetSettingQuery(decoded?._id);

    useEffect(() => {
        refetchCO2();
        refetchCounters();
        refetchTrips();
        refetchTaxiBusinessCounters();
    }, []);

    const handleExportToPDF = async () => {
        const input = reportsContainerRef.current;

        // Toggle classes before capturing
        document.querySelectorAll('.divToShow').forEach((el) => el.classList.add('showing'));
        document.querySelectorAll('.divToHide').forEach((el) => el.classList.add('hiding'));

        // Wait for re-render
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Capture the page
        const canvas = await html2canvas(input, {
            scale: 2,
            useCORS: true,
            logging: false,
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save('report.pdf');

        // Revert visibility
        document.querySelectorAll('.divToShow').forEach((el) => el.classList.remove('showing'));
        document.querySelectorAll('.divToHide').forEach((el) => el.classList.remove('hiding'));
    };

    return (
        <>
            <div ref={reportsContainerRef}>
                <div className="divToShow">
                    <Row style={{ paddingBottom: '10px', paddingTop: '10px' }}>
                        <Col>
                            <img style={{ width: '350px', height: 'auto' }} src={'assets/stasia_logo.png'} />
                        </Col>
                        <Col offset={16} span={8} style={{ textAlign: 'left' }}>
                            {data?.operatorId && (
                                <>
                                    <b>Operator:</b> {data?.operatorId?.name}
                                    <br />
                                    <b>Address:</b> {data?.operatorId?.address?.street}
                                    <br />
                                    <b>Email:</b> {data?.operatorId?.email}
                                    <br />
                                    <b>Tel:</b> {data?.operatorId?.phone}
                                    <br />
                                    <b>MF. :</b> {data?.operatorId?.tva}
                                </>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24} style={{ textAlign: 'center', paddingTop: '60px', paddingBottom: '20px' }}>
                            <Title level={2} style={{ marginBottom: 0 }}>
                                Reports & Analytics
                            </Title>
                            <span>{new Date().toLocaleString()}</span>
                        </Col>
                    </Row>
                </div>

                <div className="divToHide">
                    <Row style={{ paddingBottom: '10px', paddingTop: '10px' }}>
                        <Col span={12}>
                            <Title level={3}>Report & Analyses</Title>
                        </Col>
                    </Row>

                    <Row justify={'end'}>
                        <Button
                            type="primary"
                            shape="round"
                            icon={<DownloadOutlined />}
                            size={'middle'}
                            onClick={handleExportToPDF}
                        >
                            Export
                        </Button>
                    </Row>

                    <Row gutter={[16, 6]} style={{ paddingBottom: '10px', paddingTop: '20px' }}>
                        <ReportsFilter />
                    </Row>
                </div>
                {data?.operatorId.type === 'STASIA Mobility' && (
                    <div className="mobility-section" style={{ marginBottom: '40px' }}>
                        <div className="cards-section">
                            <Title level={3} style={{ marginBottom: '12px' }}>
                                Taxi-Mobility Business
                            </Title>

                            <Spin spinning={loadingTaxiBusinessCounters}>
                                <CountersHeaderTaxi data={countersTaxiBusinessData} />
                            </Spin>
                        </div>

                        <div className="status-section">
                            <DriversStatus data={countersTaxiBusinessData} title="Taxi Drivers" />
                        </div>
                    </div>
                )}
                {/* MICRO-MOBILITY SECTION */}
                <div className="mobility-section" style={{ marginBottom: '10px' }}>
                    <div className="cards-section">
                        <Title level={3} style={{ marginBottom: '12px' }}>
                            Micro-Mobility Business
                        </Title>
                        <Spin spinning={loadingCounters}>
                            <CountersHeader data={countersData} />
                        </Spin>
                    </div>

                    <div className="status-section">
                        <LoginMethodRidersChart data={countersData} />
                    </div>

                    <div className="status-section">
                        <RidersStatus data={countersTaxiBusinessData} title="Riders" />
                    </div>
                </div>
                <Row>
                    <Col span={14}>
                        <h2>
                            Trips /{' '}
                            {selectedDateRange?.fromDate === undefined && quickCalendarFilter === 'Default'
                                ? 'past 6 months'
                                : quickCalendarFilter}
                            {selectedDateRange?.fromDate !== undefined
                                ? `${selectedDateRange?.fromDate} to ${selectedDateRange?.toDate}`
                                : ''}
                        </h2>
                        <Spin spinning={loadingTrips}>
                            <TripsLineChart data={tripsData} />
                        </Spin>
                    </Col>
                </Row>

                <Row style={{ pageBreakBefore: 'always', paddingTop: '40px' }}>
                    <Col span={14}>
                        <h2>
                            Revenue /{' '}
                            {selectedDateRange?.fromDate === undefined && quickCalendarFilter === 'Default'
                                ? 'past 6 months'
                                : quickCalendarFilter}
                            {selectedDateRange?.fromDate !== undefined
                                ? `${selectedDateRange?.fromDate} to ${selectedDateRange?.toDate}`
                                : ''}
                        </h2>
                        <RevenueBarChart />
                    </Col>
                </Row>

                <Row style={{ paddingTop: '50px' }}>
                    <Col span={14}>
                        <h2>
                            Saved Emissions CO2(g) /{' '}
                            {selectedDateRange?.fromDate === undefined && quickCalendarFilter === 'Default'
                                ? 'past 12 months'
                                : quickCalendarFilter}
                            {selectedDateRange?.fromDate !== undefined
                                ? `${selectedDateRange?.fromDate} to ${selectedDateRange?.toDate}`
                                : ''}
                        </h2>
                        <Spin spinning={loadingCO2}>
                            <CO2LineChart data={CO2Data} />
                        </Spin>
                        <h4>Estimation factors:</h4>
                        <h5 style={{ marginTop: '-17px' }}>
                            <p>
                                The average CO2 per km for ICEVs: 115 g CO2/km (
                                <a href="https://theicct.org/wp-content/uploads/2022/08/co2-new-passenger-cars-europe-aug22.pdf">
                                    WLTP 2021
                                </a>
                                )<br />
                                The average CO2 per km for EVs: 35 - 67 g CO2/km (
                                <a href="https://www.cenex.co.uk/app/uploads/2020/08/Maximising-the-benefits-of-e-scooter-deployment-in-cities.pdf">
                                    Cenex 2020
                                </a>
                                )
                            </p>
                        </h5>
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default ReportsView;
